import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ColumnDef } from '@tanstack/react-table'
import { AlertCircle, AlertTriangle, CheckCircle2, Code2, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { DataTable, selectionColumn } from '@/components/ui/data-table'
import { isVideoAsset } from '@/services/assets/assetsApi'
import type { Layout } from '@/services/sites/layoutsApi'
import { withScheme } from '@/utils/url'
import {
  canHalveSize,
  creativeSize,
  dimensionsAllowed,
  draftProblem,
  layoutCategories,
  layoutCategoryKey,
  layoutFits,
  suggestLayout,
  unfilledComponents,
  urlComponentOf,
  type BulkCreativeDraft,
} from '@/services/creatives/bulkUpload'

export interface BulkCreativeTableProps {
  drafts: BulkCreativeDraft[]
  /** The layouts of the kind being uploaded. */
  layouts: Layout[]
  categoryLabel: (category: string) => string
  /** Sizes the line item's marketplace product allows; empty allows any. */
  allowedDimensions: number[][]
  selection: BulkCreativeDraft[]
  onSelectionChange: (rows: BulkCreativeDraft[]) => void
  onChange: (id: string, patch: Partial<BulkCreativeDraft>) => void
  onRemove: (id: string) => void
  disabled?: boolean
}

function Thumbnail({ draft }: { draft: BulkCreativeDraft }) {
  const box =
    'flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted'
  if (draft.tag !== undefined) {
    return (
      <span className={box}>
        <Code2 className="size-5 text-muted-foreground" />
      </span>
    )
  }
  if (draft.status === 'uploading') {
    return (
      <span className={`${box} flex-col gap-1 px-1.5`}>
        <span className="h-1 w-full overflow-hidden rounded-full bg-border">
          <span
            className="block h-full rounded-full bg-primary transition-all"
            style={{ width: `${draft.progress}%` }}
          />
        </span>
      </span>
    )
  }
  const asset = draft.asset
  if (!asset) {
    return (
      <span className={box}>
        <AlertCircle className="size-5 text-destructive" />
      </span>
    )
  }
  const src = isVideoAsset(asset.mimeType) ? asset.cdnId : asset.previewCdnId || asset.cdnId
  return (
    <span className={box}>
      {isVideoAsset(asset.mimeType) ? (
        <video
          src={src}
          className="size-full object-contain"
          muted
          playsInline
          preload="metadata"
        />
      ) : (
        <img src={src} alt={draft.fileName} className="size-full object-contain" />
      )}
    </span>
  )
}

/** The files of a bulk upload, each editable before the creatives are made. */
export function BulkCreativeTable({
  drafts,
  layouts,
  categoryLabel,
  allowedDimensions,
  selection,
  onSelectionChange,
  onChange,
  onRemove,
  disabled,
}: BulkCreativeTableProps) {
  const { t } = useTranslation()

  // flexRender mounts each cell function as a component, so every prop below
  // has to keep its identity between renders — a rebuilt column remounts its
  // inputs and takes the focus out of them mid-typing.
  const columns = useMemo<ColumnDef<BulkCreativeDraft, unknown>[]>(() => {
    const layoutOf = (draft: BulkCreativeDraft) => layouts.find((l) => l.id === draft.layoutId)
    const fittingLayouts = (draft: BulkCreativeDraft) => layouts.filter((l) => layoutFits(l, draft))

    return [
      selectionColumn<BulkCreativeDraft>(),
      {
        id: 'file',
        header: t('creativeUpload.file'),
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <Thumbnail draft={row.original} />
            <span
              className="max-w-40 truncate text-xs text-muted-foreground"
              title={row.original.fileName}
            >
              {row.original.fileName}
            </span>
          </div>
        ),
      },
      {
        id: 'name',
        header: t('common.name'),
        meta: { minWidth: 200 },
        enableSorting: false,
        cell: ({ row }) => (
          <Input
            value={row.original.name}
            disabled={disabled}
            aria-label={t('common.name')}
            aria-invalid={!row.original.name.trim() || undefined}
            onChange={(e) => onChange(row.original.id, { name: e.target.value })}
          />
        ),
      },
      {
        id: 'size',
        header: t('creatives.size'),
        enableSorting: false,
        cell: ({ row }) => {
          const draft = row.original
          if (!draft.width && !draft.height) return <span className="text-muted-foreground">—</span>
          const { width, height } = creativeSize(draft)
          const allowed = dimensionsAllowed(allowedDimensions, width, height)
          return (
            <div className="flex items-center gap-2 whitespace-nowrap tabular-nums">
              <span className="flex flex-col leading-tight">
                <span className="flex items-center gap-1">
                  {width}×{height}
                  {!allowed && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertTriangle className="size-3.5 text-(--ad-warning)" />
                      </TooltipTrigger>
                      <TooltipContent>{t('creativeUpload.sizeNotAllowed')}</TooltipContent>
                    </Tooltip>
                  )}
                </span>
                {draft.halfSize && (
                  <span className="text-xs text-muted-foreground">
                    {t('creativeUpload.fromFileSize', { width: draft.width, height: draft.height })}
                  </span>
                )}
              </span>
              {canHalveSize(draft) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant={draft.halfSize ? 'default' : 'outline'}
                      aria-pressed={draft.halfSize}
                      aria-label={t('creativeUpload.halfSize')}
                      disabled={disabled}
                      className="h-6 px-1.5 text-xs"
                      onClick={() => onChange(draft.id, { halfSize: !draft.halfSize })}
                    >
                      2×
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('creativeUpload.halfSizeHelp')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          )
        },
      },
      {
        id: 'category',
        header: t('creativeUpload.category'),
        meta: { minWidth: 160 },
        enableSorting: false,
        cell: ({ row }) => {
          const draft = row.original
          const categories = layoutCategories(fittingLayouts(draft))
          return (
            <Select
              label={t('creativeUpload.category')}
              value={draft.category}
              disabled={disabled || draft.status !== 'ready' || !categories.length}
              onValueChange={(category) => {
                const current = layoutOf(draft)
                const keep = current && layoutCategoryKey(current) === category
                const next = keep ? current : suggestLayout(layouts, { ...draft, category })
                onChange(draft.id, { category, layoutId: next?.id ?? '' })
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('creativeUpload.selectCategory')}>
                  {draft.category
                    ? categoryLabel(draft.category)
                    : t('creativeUpload.selectCategory')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {categoryLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        },
      },
      {
        id: 'layout',
        header: t('creatives.layout'),
        meta: { minWidth: 200 },
        enableSorting: false,
        cell: ({ row }) => {
          const draft = row.original
          const options = fittingLayouts(draft).filter(
            (l) => !draft.category || layoutCategoryKey(l) === draft.category
          )
          return (
            <Select
              label={t('creatives.layout')}
              value={draft.layoutId}
              disabled={disabled || draft.status !== 'ready' || !options.length}
              onValueChange={(layoutId) => onChange(draft.id, { layoutId })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('creatives.selectLayout')}>
                  {layoutOf(draft)?.name ?? t('creatives.selectLayout')}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {options.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        },
      },
      {
        id: 'url',
        header: t('creativeUpload.link'),
        meta: { minWidth: 200 },
        enableSorting: false,
        cell: ({ row }) => {
          const draft = row.original
          const layout = layoutOf(draft)
          const takesLink = !layout || !!urlComponentOf(layout)
          return (
            <Input
              type="url"
              value={draft.url}
              disabled={disabled || !takesLink}
              aria-label={t('creativeUpload.link')}
              placeholder={takesLink ? 'https://' : t('creativeUpload.noLinkField')}
              onChange={(e) => onChange(draft.id, { url: e.target.value })}
              onBlur={(e) => {
                const url = withScheme(e.target.value)
                if (url !== draft.url) onChange(draft.id, { url })
              }}
            />
          )
        },
      },
      {
        id: 'status',
        header: t('common.status'),
        enableSorting: false,
        cell: ({ row }) => {
          const draft = row.original
          const layout = layoutOf(draft)
          const problem = draftProblem(draft, layout)
          if (problem === 'uploading') {
            return (
              <span className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" />
                {draft.progress}%
              </span>
            )
          }
          if (problem) {
            const text =
              problem === 'failed'
                ? (draft.error ?? t('creatives.uploadFailed'))
                : t(`creativeUpload.problem.${problem}`)
            return (
              <span className="flex items-center gap-1.5 text-destructive">
                <AlertCircle className="size-3.5 shrink-0" />
                <span className="max-w-48 text-xs">{text}</span>
              </span>
            )
          }
          const missing = layout ? unfilledComponents(layout, draft) : []
          if (missing.length) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-(--ad-warning)">
                    <AlertTriangle className="size-3.5" />
                    <span className="text-xs">
                      {t('creativeUpload.needsMore', { count: missing.length })}
                    </span>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t('creativeUpload.needsMoreHelp', {
                    fields: missing.map((c) => c.name || c.tag).join(', '),
                  })}
                </TooltipContent>
              </Tooltip>
            )
          }
          return (
            <CheckCircle2
              className="size-4 text-(--ad-success)"
              aria-label={t('creativeUpload.ready')}
            />
          )
        },
      },
      {
        id: 'remove',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={t('common.remove')}
            onClick={() => onRemove(row.original.id)}
          >
            <X className="size-4" />
          </Button>
        ),
      },
    ]
  }, [t, layouts, categoryLabel, allowedDimensions, onChange, onRemove, disabled])

  return (
    <DataTable
      columns={columns}
      data={drafts}
      selection={selection}
      onSelectionChange={onSelectionChange}
      inset
    />
  )
}
